/**
 * Sets up vue apps accross a page
 * 
 * Setup: container nodes required attributes:
 *  - id: beginning with "App*" and match an available root component name (e.g. AppTheCart)
 *  - data-vue: JSON object with
 *    - "plugins": an array of vuePlugins by key
 *    - "filters": an array of vueFilters by key
 *    - "directive": an array of vueDirectives by name
 *    - "shopifyData": an object of props to inject onto the app's components
 */

// /* global Shopify */

import { debug } from '../modules/util'
import { createApp } from 'vue'
import { store } from '../../vue/store'
import * as currency from '@shopify/theme-currency'
// import Vue3TouchEvents from 'vue3-touch-events'

/**
 * Apps used on every page
 */
import AppHeader from '../../vue/AppHeader.vue'
import AppCartToggle from '../../vue/AppCartToggle.vue'
import AppOverlayCart from '../../vue/AppOverlayCart.vue'

const globalRootComponents = {
  AppHeader,
  AppCartToggle,
  AppOverlayCart
}

const name = 'VueApps'

const vuePlugins = { store }

const vueFilters = {
  shopifyImgUrl: function (value, size) {
    return `${value}&width=${size}`
  },
  shopifyMoney: function (cents) {
    return currency.formatMoney(cents, window.theme.vue.shopifyData.filters.money_format).replace('.00', '')
  }
}

const vueDirectives = {
  lazy: {
    mounted:
      window.theme.hasNativeLazyloading
        ? (el) => window.theme.initLazyImages(el)
        : () => {}
  },
  loading: {
    mounted: () => window.theme.loading.complete()
  }
  // touch: Vue3TouchEvents
}

function initVueApps () {
  debug(`${name}: init()`)

  store.dispatch('updateCart')
  mountApps()
}

async function mountApps () {
  debug(`${name}: mountApps()`)

  const apps = document.querySelectorAll('[data-vue]')
  if (apps.length) {
    for (const rootElement of apps) {
      const rootId = rootElement.id
      if (typeof rootId !== 'undefined') {
        const filename = rootId.replace('Vue', '')

        /** already imported */
        if (Object.keys(globalRootComponents).includes(filename)) {
          initAppFromElement(rootElement, globalRootComponents[filename])

        /** dynamic import */
        } else {
          const rootComponent = await import(`../../vue/${filename}.vue`)
          initAppFromElement(rootElement, rootComponent.default)
        }
      }
    }
  }
}

function initAppFromElement (element, rootComponent) {
  // debug(`${name}: initAppFromElement`, element)
  
  const props = element.getAttribute('data-vue')
  const propsJSON = props && props.length ? JSON.parse(props) : {}
  
  // debug(`${name}: initAppFromElement.propsJSON`, propsJSON)

  const plugins = propsJSON.plugins
    ? propsJSON.plugins.map(p => vuePlugins[p] || {})
    : []

  const filters = propsJSON.filters
    ? Object.fromEntries(
      propsJSON.filters.map(f => [f, vueFilters[f]] || [])
    )
    : {}
  const directives = propsJSON.directives || []
  const shopifyData = propsJSON.shopifyData || {}
  
  const app = createApp(rootComponent)

  /** register any plugins */
  if (plugins.length) {
    plugins.forEach(p => app.use(p))
  }

  /** register any directives */
  if (directives.length) {
    directives.forEach((d) => {
      const targetDirective = vueDirectives[d]
      debug(`${name}: register directive ${targetDirective}`)
      if (typeof targetDirective !== 'undefined') {
        app.directive(d, targetDirective)
      }
    })
  }

  /** register any filters */
  if (Object.keys(filters).length) {
    app.config.globalProperties.$filters = filters
  }

  /**
   * make the global window variable available,
   * allowing passage of shopify language settings
   * from liquid templates to vue components
   */
  app.config.globalProperties.window = window

  app.config.errorHandler = (err, instance, info) => {
    console.error(`${name}: error in vue app.`, err, instance, info)
  }

  /** make shopifyData available to all child components */
  app.provide('shopifyData', shopifyData)

  app.mount(element)

  /** remove data attribute in production */
  if (process.env.NODE_ENV === 'production') {
    element.removeAttribute('data-vue')
  }

  /** unmount when shopify editor preview reloads */
  // if (Shopify.designMode) {
  //   const handleShopifyUnload = () => {
  //     app.unmount()
  //     window.removeEventListener('shopify:section:unload', handleShopifyUnload)
  //   }
    
  //   window.addEventListener('shopify:section:unload', handleShopifyUnload)
  // }
}

export {
  initVueApps
}